import React, { useEffect, useMemo, useState } from 'react';
import { toast as message } from 'sonner';
import { supabaseClient } from '../../utils/supabase';
import useAuth from '../../utils/useAuth';
import EnhancedDialog from '../EnhancedDialog';
import { properName, sendFundingPackage } from './offerPackage';
import { fundedFollowupHtml, fundedFollowupSubject } from './fundedFollowup';
import { RecipientList, RecipientTo, normalizeEmail } from './RecipientEditor';
import { useMerchantEmails } from './useMerchantEmails';
import './SendOfferDialog.css';

const ADMIN_BCC = 'admin@skylinecaps.com';

export interface SendFundedRow {
  id?: unknown;
  merchant?: unknown;
  email?: unknown;
  first_name?: unknown;
  merchant_label?: unknown;
  amount?: unknown;
  date_funded?: unknown;
  funder_label?: unknown;
  main_rep?: unknown;
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function parseDateOnly(value: unknown): Date | null {
  const s = asString(value);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/**
 * Funded-grid row action: preview + send the "We Funded You" renewal
 * follow-up. All merge data comes off the report row; the deal rep's email
 * (for the Bcc) and the sender's phone are fetched lazily on open.
 */
export function SendFundedDialog({
  row,
  onClose,
}: {
  row: Record<string, unknown> | null;
  onClose: () => void;
}): React.ReactElement | null {
  const { appUser } = useAuth();
  const typedRow = row as SendFundedRow | null;
  const [repPhone, setRepPhone] = useState<string | null>(null);
  const [dealRepEmail, setDealRepEmail] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  // Editable envelope (REG-675): To limited to the merchant's emails on file,
  // Bcc to internal addresses; admin@ and the sender copy are locked.
  const [toEmail, setToEmail] = useState('');
  const [extraBcc, setExtraBcc] = useState<string[]>([]);
  const [dealRepRemoved, setDealRepRemoved] = useState(false);
  // Editable subject (REG-681); re-seeded from the row on every open.
  const [subjectDraft, setSubjectDraft] = useState<string | null>(null);

  const isOpen = row != null;
  const rowEmail = asString(typedRow?.email).trim().toLowerCase();
  const merchantEmails = useMerchantEmails(isOpen ? (typeof typedRow?.merchant === 'number' ? typedRow.merchant : null) : null, rowEmail);
  const merchantEmail = toEmail || rowEmail;
  const businessName = asString(typedRow?.merchant_label) || 'this merchant';
  const dateFunded = parseDateOnly(typedRow?.date_funded);
  const amount = typeof typedRow?.amount === 'number' ? typedRow.amount : 0;

  useEffect(() => {
    if (!isOpen) return;
    setSending(false);
    setDealRepEmail(null);
    setToEmail(asString(typedRow?.email).trim().toLowerCase());
    setExtraBcc([]);
    setDealRepRemoved(false);
    setSubjectDraft(null);
  }, [row, isOpen]);

  // Sender's phone for the signature (their rep record).
  useEffect(() => {
    if (!isOpen || !appUser?.id) return;
    let cancelled = false;
    supabaseClient
      .from('rep')
      .select('phone')
      .eq('app_user', appUser.id)
      .limit(1)
      .then(({ data }) => {
        if (!cancelled) setRepPhone(data?.[0]?.phone ?? null);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, appUser?.id]);

  // Deal rep's email for the Bcc: rep.app_user -> core_crm.app_user.email.
  useEffect(() => {
    const mainRep = typeof typedRow?.main_rep === 'number' ? typedRow.main_rep : null;
    if (!isOpen || mainRep == null) return;
    let cancelled = false;
    (async () => {
      const { data: rep } = await supabaseClient
        .from('rep').select('app_user').eq('id', mainRep).maybeSingle();
      if (cancelled || !rep?.app_user) return;
      const { data: user } = await supabaseClient
        .schema('core_crm').from('app_user').select('email').eq('id', rep.app_user).maybeSingle();
      if (!cancelled && user?.email) setDealRepEmail(String(user.email).toLowerCase());
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, typedRow?.main_rep]);

  const senderEmail = normalizeEmail(appUser?.email ?? '');
  // Admin + the sender are always on the envelope (the server re-adds them
  // regardless); the deal's rep and any extra internal addresses are the
  // rep-editable part.
  const bccLocked = useMemo(() => [...new Set([ADMIN_BCC, ...(senderEmail ? [senderEmail] : [])])], [senderEmail]);
  const bccEditable = useMemo(() => {
    const list: string[] = [];
    if (dealRepEmail && !dealRepRemoved && !bccLocked.includes(dealRepEmail)) list.push(dealRepEmail);
    for (const email of extraBcc) if (!list.includes(email) && !bccLocked.includes(email)) list.push(email);
    return list;
  }, [dealRepEmail, dealRepRemoved, extraBcc, bccLocked]);
  const bcc = useMemo(() => [...bccLocked, ...bccEditable], [bccLocked, bccEditable]);

  const previewHtml = useMemo(() => {
    if (!typedRow || !dateFunded) return '';
    const rep = appUser?.email
      ? { name: appUser.name ?? '', email: appUser.email, phone: repPhone ?? undefined }
      : null;
    return fundedFollowupHtml(
      {
        firstName: asString(typedRow.first_name),
        amount,
        dateFunded,
        funderName: asString(typedRow.funder_label),
      },
      rep,
    );
  }, [typedRow, dateFunded, amount, appUser?.email, appUser?.name, repPhone]);

  if (!isOpen) return null;

  const defaultSubject = fundedFollowupSubject(asString(typedRow?.first_name));
  const subject = subjectDraft ?? defaultSubject;
  const canSend = Boolean(merchantEmail && dateFunded && amount > 0 && subject.trim());

  const handleSend = async () => {
    const merchantId = typeof typedRow?.merchant === 'number' ? typedRow.merchant : null;
    if (!canSend || !appUser?.email || merchantId == null || sending) return;
    setSending(true);
    try {
      const result = await sendFundingPackage({
        merchantId,
        merchantEmail,
        html: previewHtml,
        subject: subject.trim(),
        bcc,
        suppressCc: true,
      });
      if (!result.ok) throw new Error(result.error || 'The email service rejected the send');
      message.success(`Follow-up sent to ${merchantEmail} (from ${result.from ?? appUser.email}, Bcc ${bcc.join(', ')})`);
      onClose();
    } catch (err: unknown) {
      message.error(err instanceof Error ? err.message : 'Could not send the follow-up');
    } finally {
      setSending(false);
    }
  };

  return (
    <EnhancedDialog
      isOpen={isOpen}
      onClose={sending ? () => {} : onClose}
      title={`We Funded You — ${businessName}`}
      className="send-offer-dialog"
      // Reps lose in-progress terms/prepays edits on accidental dismissal:
      // these email dialogs only close through Cancel / the X (REG: Zack 8/28).
      canOutsideClickClose={false}
      canEscapeKeyClose={false}
    >
      <div className="sky-dialog-body">
        {!merchantEmail && (
          <div className="sky-callout sky-callout-warning" style={{ marginBottom: 12 }}>
            This merchant has no email address on file — add one to the merchant record before sending.
          </div>
        )}
        {!dateFunded && (
          <div className="sky-callout sky-callout-warning" style={{ marginBottom: 12 }}>
            This deal has no funded date, so the statement months can&#39;t be computed.
          </div>
        )}
        <div className="send-offer-envelope">
          <RecipientTo options={merchantEmails} value={merchantEmail} onChange={setToEmail} disabled={sending} />
          <div><span className="send-offer-envelope-label">From</span> {properName(appUser?.name ?? '')} &lt;{appUser?.email}&gt;</div>
          <RecipientList
            label="Bcc"
            locked={bccLocked}
            editable={bccEditable}
            disabled={sending}
            onChange={(next) => {
              if (dealRepEmail) setDealRepRemoved(!next.includes(dealRepEmail));
              setExtraBcc(next.filter((email) => email !== dealRepEmail));
            }}
          />
          <div className="send-offer-recipients">
            <span className="send-offer-envelope-label">Subject</span>
            <input
              className="send-offer-subject-input"
              aria-label="Email subject"
              value={subject}
              maxLength={200}
              disabled={sending}
              onChange={(e) => setSubjectDraft(e.target.value)}
            />
          </div>
        </div>
        <iframe className="send-offer-preview" title="Funded follow-up email preview"
          sandbox="" srcDoc={previewHtml} />
      </div>
      <div className="sky-dialog-footer">
        <button className="sky-btn sky-btn-ghost" onClick={onClose} disabled={sending}>Cancel</button>
        <button className="sky-btn sky-btn-primary" onClick={handleSend} disabled={sending || !canSend} aria-busy={sending}>
          {sending ? <span className="sky-spinner" /> : null}
          {sending ? 'Sending…' : 'Send email'}
        </button>
      </div>
    </EnhancedDialog>
  );
}

export default SendFundedDialog;
